import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { X, GripVertical } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { uploadEventImages, uploadGifWithThumbnail } from "../../services/createEventService";
import { resolveUserCountryCode } from "../../utils/helpers";
import { useAuth } from "../../context/AuthContext";
import { getGifDurationSeconds } from "../../utils/gifDuration";
import { MAX_GIF_SIZE_BYTES, MAX_GIF_DURATION_SECONDS } from "../../utils/gifConstraints";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * Event title image/GIF picker.
 * Mirrors the mobile app's image-picker/index.js: up to 3 images OR exactly
 * 1 GIF (mutually exclusive), downscale image long side to ~1999px before
 * upload, POST to Image/BulkCompressed with folderName='Event_Title',
 * exposes {url, thumbnail} pairs.
 *
 * GIFs use a *different* upload path, matching mobile's
 * convert-video-to-gif/index.js exactly: `Image/Bulk` (not BulkCompressed —
 * that endpoint re-encodes to a static webp, silently killing animation),
 * uploaded together with a static first-frame thumbnail generated here on
 * canvas (mobile generates it with ffmpeg). The final image list carries
 * *two* entries for a GIF — the animated file and the static thumbnail —
 * same as mobile's getImages() in gif mode.
 *
 * One scope difference: the in-app video-to-GIF conversion tool
 * (ConvertVideoToGif) isn't ported — this picks an existing .gif from disk.
 */

const MAX_IMAGES = 3;
const MAX_LONG_SIDE = 1999;
const SKIP_COMPRESSION_UNDER_BYTES = 0.5 * 1024 * 1024;
const STATIC_THUMBNAIL_WIDTH = 480;

let _idCounter = 0;
const nextId = () => `img-${Date.now()}-${++_idCounter}`;

const isGifUrl = (url) => /\.gif(\?|$)/i.test(String(url || ""));

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

/** Downscale a File on canvas if its long side exceeds MAX_LONG_SIDE. Falls back to the original file on any error. */
const downscaleImage = async (file) => {
  if (!file.type?.startsWith("image/") || file.type === "image/gif") return file;
  if (file.size < SKIP_COMPRESSION_UNDER_BYTES) return file;

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    const { width, height } = img;
    if (width <= MAX_LONG_SIDE && height <= MAX_LONG_SIDE) return file;

    const scale = MAX_LONG_SIDE / Math.max(width, height);
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
    if (!blob) return file;
    return new File([blob], file.name, { type: "image/jpeg" });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/** First-frame static JPEG thumbnail for a GIF — web equivalent of mobile's ffmpeg-based buildStaticThumbnailPayload. */
const generateGifStaticThumbnail = async (gifFile) => {
  const objectUrl = URL.createObjectURL(gifFile);
  try {
    const img = await loadImage(objectUrl); // <img> paints the first frame on load, before it starts animating
    const scale = STATIC_THUMBNAIL_WIDTH / img.width;
    const targetW = Math.min(img.width, STATIC_THUMBNAIL_WIDTH);
    const targetH = Math.round(img.height * (targetW / img.width) || img.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH || img.height;
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
    if (!blob) return null;
    return new File([blob], `gif_thumb_${Date.now()}.jpg`, { type: "image/jpeg" });
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/** Same fallback field-name chain as mobile's resolveUploadedUrl/resolveUploadedThumbnailUrl. */
const resolveUploadedUrl = (item) =>
  item?.originalUrl ?? item?.OriginalUrl ?? item?.original ?? item?.Original ?? item?.url ?? item?.Url ?? "";
const resolveUploadedThumbnailUrl = (item) =>
  item?.compressedUrl ?? item?.CompressedUrl ?? resolveUploadedUrl(item);

/**
 * A stored/draft GIF is represented as *two* {url, thumbnail} entries (the
 * animated file + the static thumbnail — see getImages()). Naively mapping
 * each entry 1:1 into picker state would render two separate tiles, so this
 * detects that shape first and collapses it back into one 'gif' entry.
 */
const hydrateInitialImages = (rawImages) => {
  const gifSource = rawImages.find((img) => isGifUrl(img.url));
  if (gifSource) {
    const staticSource = rawImages.find((img) => img !== gifSource);
    const staticThumbnailUrl = staticSource?.url || gifSource.thumbnail || null;
    return [
      {
        id: nextId(),
        previewUrl: staticThumbnailUrl || gifSource.url,
        url: gifSource.url,
        thumbnail: staticThumbnailUrl || gifSource.url,
        staticThumbnailUrl,
        type: "gif",
        gifMeta: null, // unknown for a hydrated/pre-existing GIF — not re-validated
        uploading: false,
      },
    ];
  }

  return rawImages.map((img) => ({
    id: nextId(),
    previewUrl: img.thumbnail || img.url,
    url: img.url,
    thumbnail: img.thumbnail,
    staticThumbnailUrl: null,
    type: "image",
    gifMeta: null,
    uploading: false,
  }));
};

/**
 * One draggable thumbnail. A native multi-file `<input>` selection doesn't
 * reliably preserve the order the promoter clicked files in (that's the
 * browser/OS's call, not ours), so instead of trusting that order for the
 * final upload, the promoter can drag thumbnails into the order they want —
 * that manual order is what actually gets saved (see EventImagePicker's
 * getImages(), which just reflects `images` state order).
 */
const SortableThumb = ({ img, draggable, onRemove, t }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: img.id, disabled: !draggable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={{ ...s.thumbWrap, ...style }}>
      <img src={img.previewUrl || img.url} alt="" style={s.thumb} />
      {draggable && !img.uploading && (
        <span {...attributes} {...listeners} style={s.dragHandle} title={t("eventImagePicker.dragToReorder")}>
          <GripVertical size={12} strokeWidth={2.5} />
        </span>
      )}
      {img.type === "gif" && !img.uploading && <span style={s.gifBadge}>{t("eventImagePicker.gifBadge")}</span>}
      {img.uploading && <div style={s.uploadingOverlay}>{t("eventImagePicker.uploading")}</div>}
      {!img.uploading && (
        <button type="button" style={s.removeBtn} onClick={() => onRemove(img.id)} title={t("common:actions.remove")}>
          <X size={12} strokeWidth={2.25} />
        </button>
      )}
    </div>
  );
};

const EventImagePicker = forwardRef(({ onChange, onLoadingChange, initialImages = [] }, ref) => {
  const { t } = useTranslation(["events", "common"]);
  const { user } = useAuth();
  const [images, setImages] = useState(() => hydrateInitialImages(initialImages));
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const gifInputRef = useRef(null);
  const previewUrlsToRevoke = useRef(new Set());
  const dragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const hasGif = images.some((i) => i.type === "gif");
  const imageOnlyCount = images.filter((i) => i.type === "image").length;
  const hasImages = imageOnlyCount > 0;

  useImperativeHandle(ref, () => ({
    getImages: () => {
      const gif = images.find((i) => i.type === "gif" && i.url);
      if (gif) {
        const entries = [{ url: gif.url, thumbnail: gif.thumbnail || gif.url }];
        if (gif.staticThumbnailUrl && gif.staticThumbnailUrl !== gif.url) {
          entries.push({ url: gif.staticThumbnailUrl, thumbnail: gif.staticThumbnailUrl });
        }
        return entries;
      }
      return images.filter((i) => i.type === "image" && i.url).map((i) => ({ url: i.url, thumbnail: i.thumbnail }));
    },
    isUploading: () => isUploading,
    getMediaType: () => (hasGif ? "gif" : "images"),
    getSelectedGifMeta: () => {
      const gif = images.find((i) => i.type === "gif");
      return gif ? { size: gif.gifMeta?.size ?? 0, duration: gif.gifMeta?.duration ?? 0 } : null;
    },
  }));

  useEffect(
    () => () => {
      previewUrlsToRevoke.current.forEach((url) => URL.revokeObjectURL(url));
    },
    []
  );

  const emitChange = useCallback(
    (next) => {
      const gif = next.find((i) => i.type === "gif" && i.url);
      if (gif) {
        onChange?.([{ url: gif.url, thumbnail: gif.thumbnail || gif.url }]);
        return;
      }
      onChange?.(next.filter((i) => i.type === "image" && i.url).map((i) => ({ url: i.url, thumbnail: i.thumbnail })));
    },
    [onChange]
  );

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type?.startsWith("image/") && f.type !== "image/gif");
    if (!files.length) return;

    if (hasGif) {
      toast.warning(t("eventImagePicker.toasts.removeGifFirst"));
      return;
    }

    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      toast.warning(t("eventImagePicker.toasts.maxImages", { max: MAX_IMAGES }));
      return;
    }
    const toAdd = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.warning(t("eventImagePicker.toasts.onlyNMore", { remaining, max: MAX_IMAGES }));
    }

    setIsUploading(true);
    onLoadingChange?.(true);

    const pending = toAdd.map((file) => {
      const previewUrl = URL.createObjectURL(file);
      previewUrlsToRevoke.current.add(previewUrl);
      return { id: nextId(), previewUrl, url: null, thumbnail: null, staticThumbnailUrl: null, type: "image", gifMeta: null, uploading: true };
    });
    setImages((prev) => [...prev, ...pending]);

    try {
      const processed = await Promise.all(toAdd.map(downscaleImage));
      const countryCode = resolveUserCountryCode(user, 49);
      const res = await uploadEventImages(processed, countryCode);

      if (res.data?.status === "Success" && Array.isArray(res.data.result)) {
        setImages((prev) => {
          const next = [...prev];
          res.data.result.forEach((r, i) => {
            const idx = next.findIndex((img) => img.id === pending[i]?.id);
            if (idx !== -1) {
              next[idx] = { ...next[idx], url: r.original, thumbnail: r.compressed || r.original, uploading: false };
            }
          });
          emitChange(next);
          return next;
        });
      } else {
        throw new Error(res.data?.message || "Upload failed");
      }
    } catch {
      toast.error(t("eventImagePicker.toasts.imageUploadFailed"));
      setImages((prev) => {
        const next = prev.filter((img) => !pending.some((p) => p.id === img.id));
        emitChange(next);
        return next;
      });
    } finally {
      setIsUploading(false);
      onLoadingChange?.(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleGifFile = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;

    if (hasImages) {
      toast.warning(t("eventImagePicker.toasts.removeImagesFirst"));
      return;
    }
    if (file.type !== "image/gif" && !/\.gif$/i.test(file.name)) {
      toast.warning(t("eventImagePicker.toasts.selectGifFile"));
      return;
    }
    if (file.size > MAX_GIF_SIZE_BYTES) {
      toast.warning(t("eventImagePicker.toasts.gifMaxSize", { size: (MAX_GIF_SIZE_BYTES / (1024 * 1024)).toFixed(0) }));
      return;
    }

    const duration = await getGifDurationSeconds(file);
    if (duration != null && duration > MAX_GIF_DURATION_SECONDS) {
      toast.warning(t("eventImagePicker.toasts.gifMaxDuration", { seconds: MAX_GIF_DURATION_SECONDS }));
      return;
    }

    setIsUploading(true);
    onLoadingChange?.(true);

    const previewUrl = URL.createObjectURL(file);
    previewUrlsToRevoke.current.add(previewUrl);
    const pendingId = nextId();
    const gifMeta = { size: file.size, duration: duration ?? 0 };
    setImages([{ id: pendingId, previewUrl, url: null, thumbnail: null, staticThumbnailUrl: null, type: "gif", gifMeta, uploading: true }]);

    try {
      const thumbnailFile = await generateGifStaticThumbnail(file);
      if (!thumbnailFile) throw new Error("Could not generate a thumbnail for this GIF.");

      const countryCode = resolveUserCountryCode(user, 49);
      const res = await uploadGifWithThumbnail(file, thumbnailFile, countryCode);
      const results = res.data?.result ?? res.data?.Result;
      const isSuccess = res.data?.status === "Success" || res.data?.Status === "Success";

      if (isSuccess && Array.isArray(results) && results.length > 1) {
        const uploadedUrl = resolveUploadedUrl(results[0]);
        const uploadedThumbnailUrl = resolveUploadedThumbnailUrl(results[1]);
        if (!uploadedUrl || !uploadedThumbnailUrl) throw new Error("GIF upload response was missing URLs.");

        setImages((prev) => {
          const next = prev.map((img) =>
            img.id === pendingId
              ? { ...img, url: uploadedUrl, thumbnail: uploadedThumbnailUrl, staticThumbnailUrl: uploadedThumbnailUrl, uploading: false }
              : img
          );
          emitChange(next);
          return next;
        });
      } else {
        throw new Error(res.data?.message || "GIF upload failed");
      }
    } catch {
      toast.error(t("eventImagePicker.toasts.gifUploadFailed"));
      setImages((prev) => {
        const next = prev.filter((img) => img.id !== pendingId);
        emitChange(next);
        return next;
      });
    } finally {
      setIsUploading(false);
      onLoadingChange?.(false);
      if (gifInputRef.current) gifInputRef.current.value = "";
    }
  };

  const removeImage = (id) => {
    if (isUploading) {
      toast.info(t("eventImagePicker.toasts.waitForUpload"));
      return;
    }
    setImages((prev) => {
      const next = prev.filter((img) => img.id !== id);
      emitChange(next);
      return next;
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleThumbDragEnd = (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    setImages((prev) => {
      const oldIndex = prev.findIndex((img) => img.id === active.id);
      const newIndex = prev.findIndex((img) => img.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      emitChange(next);
      return next;
    });
  };

  return (
    <div style={s.wrap}>
      <label style={s.label}>
        {t("eventImagePicker.label")} {hasGif ? null : <span style={s.count}>({images.length}/{MAX_IMAGES})</span>}
      </label>

      <div
        style={{ ...s.row, ...(isDragOver ? s.rowDragOver : {}) }}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={onDrop}
      >
        <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={handleThumbDragEnd}>
          <SortableContext items={images.map((img) => img.id)} strategy={rectSortingStrategy}>
            {images.map((img) => (
              <SortableThumb
                key={img.id}
                img={img}
                draggable={imageOnlyCount > 1 && img.type === "image"}
                onRemove={removeImage}
                t={t}
              />
            ))}
          </SortableContext>
        </DndContext>

        {images.length === 0 && (
          <div style={s.emptyHint}>{t("eventImagePicker.emptyHint")}</div>
        )}
      </div>

      <div style={s.actionsRow}>
        <button
          type="button"
          style={{ ...s.actionBtn, ...((isUploading || hasGif || imageOnlyCount >= MAX_IMAGES) ? s.actionBtnDisabled : {}) }}
          disabled={isUploading || hasGif || imageOnlyCount >= MAX_IMAGES}
          onClick={() => fileInputRef.current?.click()}
        >
          {isUploading ? t("eventImagePicker.uploading") : t("eventImagePicker.addImages", { count: imageOnlyCount, max: MAX_IMAGES })}
        </button>
        <button
          type="button"
          style={{ ...s.actionBtn, ...((isUploading || hasImages || hasGif) ? s.actionBtnDisabled : {}) }}
          disabled={isUploading || hasImages || hasGif}
          onClick={() => gifInputRef.current?.click()}
        >
          {isUploading ? t("eventImagePicker.uploading") : t("eventImagePicker.addGif", { count: hasGif ? 1 : 0 })}
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <input
        ref={gifInputRef}
        type="file"
        accept=".gif,image/gif"
        style={{ display: "none" }}
        onChange={(e) => handleGifFile(e.target.files)}
      />
      <p style={s.hint}>
        {hasGif
          ? t("eventImagePicker.gifSelectedHint", { size: (MAX_GIF_SIZE_BYTES / (1024 * 1024)).toFixed(0), seconds: MAX_GIF_DURATION_SECONDS })
          : imageOnlyCount > 1
            ? t("eventImagePicker.reorderHint")
            : t("eventImagePicker.defaultHint")}
      </p>
    </div>
  );
});

const s = {
  wrap: { marginBottom: 16 },
  label: { display: "block", marginBottom: 8, fontWeight: 500, fontSize: 14, color: "#444" },
  count: { fontWeight: 400, color: "#888", fontSize: 12 },
  row: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    padding: 12,
    minHeight: 44,
    border: "1.5px dashed #e5e7eb",
    borderRadius: 10,
    background: "#fafafa",
    transition: "border-color 0.15s ease",
  },
  rowDragOver: { borderColor: "#6c63ff", background: "#f4f3ff" },
  emptyHint: { fontSize: 12, color: "#aaa", alignSelf: "center", padding: "8px 4px" },
  thumbWrap: {
    position: "relative",
    width: 96,
    height: 96,
    borderRadius: 8,
    overflow: "hidden",
    background: "#eee",
    flexShrink: 0,
  },
  thumb: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  gifBadge: {
    position: "absolute",
    bottom: 4,
    left: 4,
    background: "rgba(0,0,0,0.65)",
    color: "#fff",
    fontSize: 9,
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: 4,
    letterSpacing: 0.5,
  },
  uploadingOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    color: "#fff",
    fontSize: 11,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  dragHandle: {
    position: "absolute",
    top: 4,
    left: 4,
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: "rgba(0,0,0,0.6)",
    color: "#fff",
    cursor: "grab",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  removeBtn: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 20,
    height: 20,
    borderRadius: "50%",
    border: "none",
    background: "rgba(0,0,0,0.6)",
    color: "#fff",
    fontSize: 11,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  },
  actionsRow: { display: "flex", gap: 10, marginTop: 10 },
  actionBtn: {
    flex: 1,
    padding: "9px 12px",
    borderRadius: 8,
    border: "1.5px solid #c7c9d9",
    background: "#fff",
    color: "#6c63ff",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  actionBtnDisabled: { opacity: 0.5, cursor: "not-allowed", color: "#a0a0a0" },
  hint: { fontSize: 11, color: "#999", marginTop: 6 },
};

export default EventImagePicker;
